import { Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
const PieChartView = ({ data, columns, selectedTab }) => {
  const [chartOptions, setChartOptions] = useState({});
  const [series, setSeries] = useState([]);

  const { t } = useTranslation();
 const dynamicColors = [
  "#1976D2", // Blue
  "#2E7D32", // Green
  "#F57C00", // Orange
  "#C62828", // Red
  "#6A1B9A", // Purple
  "#00838F", // Teal
  "#8D6E63", // Brown
  "#C0CA33", // Lime
  "#E91E63", // Pink
  "#5D4037", // Dark Brown
  "#3949AB", // Indigo
  "#FBC02D", // Yellow
  "#7B1FA2", // Violet
  "#43A047", // Emerald
  "#EF6C00", // Deep Orange
  "#546E7A", // Blue Grey
  "#D81B60", // Magenta
  "#00ACC1", // Cyan
];
  // Helper function to safely handle data
  const getDataRows = () => (Array.isArray(data) ? data : data.rows || []);

  // Function to handle revenue breakdown by room type
  const handleRevenueBreakdown = (data, columns) => {
    const rows = getDataRows();
    const dateIndex = columns.findIndex((col) => col.field === "column1");
    const roomsSoldIndex = columns.findIndex((col) => col.field === "column8");
    const roomTypeColumns = columns.slice(dateIndex + 1, roomsSoldIndex);

    const labels = roomTypeColumns.map((col) => col.headerName);
    const revenueData = roomTypeColumns.map((col) =>
      rows
        .reduce(
          (total, row) =>
            total +
            Number.parseFloat(
              String(row[col.field] || "").replaceAll(/[^0-9.-]+/g, "") || 0
            ),
          0
        )
        ?.toFixed(2)
    );

    setChartOptions({
      chart: {
        type: "pie",
      },
      labels: labels,
      title: {
        // text: 'Revenue Breakdown by Source',
        align: "center",
      },
      dataLabels: {
        enabled: true, // Enable labels on the chart
        style: {
          colors: ["#FFFFFF"], // Assign a color from dynamicColors
          fontSize: "14px", // Adjust font size
          fontWeight: "bold", // Adjust font weight
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000000",
          opacity: 0.5,
        },
      },
      legend: {
        position: "bottom",
        labels: {
          colors: labels.map(
            (_, index) => dynamicColors[index % dynamicColors.length]
          ), // Use different colors for legend items
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            if (val == null || Number.isNaN(val)) return "0.00";
            return Number.parseFloat(val).toFixed(2);
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    });
    const numericSeries = revenueData
      .map((value) => Number.parseFloat(value))
      .filter((val) => !Number.isNaN(val) && val != null);

    // Filter labels to match series length and remove undefined
    const validLabels = labels.filter(
      (label, idx) => label != null && idx < numericSeries.length
    );
    const validSeries = numericSeries.slice(0, validLabels.length);

    if (validLabels.length > 0 && validSeries.length > 0) {
      setChartOptions((prev) => ({ ...prev, labels: validLabels }));
      setSeries(validSeries);
    } else {
      setSeries([]);
    }
  };

  // Function to handle revenue breakdown by payment method
  const handlePaymentBreakdown = (data, columns) => {
    const totalPaymentMethods = {};
    const rows = getDataRows();

    // Process each row and accumulate the totals for each payment method
    rows.forEach((row) => {
      columns.slice(1, -1).forEach((field, index) => {
        const column = `column${index + 2}`; // Get the column name
        const amount = Number(
          String(row[column] || "")
            .replaceAll(/[$,]/g, "")
            .trim()
        );
        // Accumulate the values for each payment method
        if (!Number.isNaN(amount)) {
          if (field.headerName === "Credit Card Amount" && +amount >= 0) {
            // If it's a credit card amount and greater than 0, get the card type (e.g., Visa)
            const cardType = row["column5"]; // This field holds the credit card type like "Visa"
            if (totalPaymentMethods[cardType]) {
              totalPaymentMethods[cardType] += amount;
            } else {
              totalPaymentMethods[cardType] = amount;
            }
          } else {
            if (totalPaymentMethods[field.headerName]) {
              totalPaymentMethods[field.headerName] += amount;
            } else {
              totalPaymentMethods[field.headerName] = amount;
            }
          }
        }
      });
    });

    // Convert totalPaymentMethods object to the desired format and remove the 'Credit Card Amount' field
    const finalData = Object.keys(totalPaymentMethods)
      .filter((method) => method !== "Credit Card Amount")
      .map((method) => ({
        paymentMethod: method,
        amount: `$${totalPaymentMethods[method]?.toFixed(2) || "00.00"}`,
      }));
    // Extract labels and amounts for the chart
    const labels = finalData.map((item) => item.paymentMethod);
    const seriesData = finalData.map((item) =>
      Number.parseFloat(item.amount.replaceAll(/[^0-9.-]+/g, ""))
    );

    // Dynamic color assignment for chart labels and series
    const colors = [];
    const numLabels = labels.length;
    const numColors = dynamicColors.length;

    // If there are more data points than colors, cycle through the colors
    for (let i = 0; i < numLabels; i++) {
      colors.push(dynamicColors[i % numColors]);
    }
    setChartOptions({
      chart: {
        type: "pie",
      },
      labels: labels,
      title: {
        // text: "Revenue Breakdown by Payment Method",
        align: "center",
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#FFFFFF"],
          fontSize: "14px",
          fontWeight: "bold",
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000000",
          opacity: 0.5,
        },
      },
      legend: {
        position: "right",
        labels: {
          colors: colors,
        },
        itemMargin: {
          horizontal: 15,
          vertical: 5,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            if (val == null || Number.isNaN(val)) return "$0.00";
            return `$${Number.parseFloat(val).toFixed(2)}`;
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    });

    // Filter out invalid values
    const validLabels = labels.filter((label) => label != null);
    const validSeries = seriesData.filter((val) => !Number.isNaN(val) && val != null);

    if (validLabels.length === validSeries.length && validLabels.length > 0) {
      setChartOptions((prev) => ({ ...prev, labels: validLabels }));
      setSeries(validSeries);
    } else {
      setSeries([]);
    }
  };

  const handleTransactionBatch = (data, columns) => {
    const totalPaymentMethods = {};
    const rows = getDataRows();

    // Process each row and accumulate the totals for each payment method
    rows.forEach((row) => {
      columns.slice(1, -1).forEach((field, index) => {
        const column = `column${index + 2}`; // Get the column name
        const amount = Number(
          String(row[column] || "")
            .replaceAll(/[$,]/g, "")
            .trim()
        );
        // Accumulate the values for each payment method
        if (!Number.isNaN(amount)) {
          if (
            field.headerName === "Credit Card Amount breakdown ($)" &&
            +amount >= 0
          ) {
            // If it's a credit card amount and greater than 0, get the card type (e.g., Visa)
            const refundAmount = Number(
              String(row["column8"] || "")
                .replaceAll(/[$,]/g, "")
                .trim()
            );

            const cardType = row["column6"];

            if (cardType && cardType !== "-") {
              if (totalPaymentMethods[cardType]) {
                totalPaymentMethods[cardType] += amount - refundAmount;
              } else {
                totalPaymentMethods[cardType] = amount - refundAmount;
              }
            }
          } else {
            if (totalPaymentMethods[field.headerName]) {
              totalPaymentMethods[field.headerName] += amount;
            } else {
              totalPaymentMethods[field.headerName] = amount;
            }
          }
        }
      });
    });

    // Convert totalPaymentMethods object to the desired format and remove the 'Credit Card Amount breakdown ($)' field
    const finalData = Object.keys(totalPaymentMethods)
      .filter((method) => method !== "Credit Card Amount breakdown ($)")
      .map((method) => ({
        paymentMethod: method,
        amount: `$${totalPaymentMethods[method]?.toFixed(2) || "00.00"}`,
      }));
    // Extract labels and amounts for the chart
    const labels = finalData.map((item) => item.paymentMethod);
    const seriesData = finalData.map((item) =>
      Number.parseFloat(item.amount.replaceAll(/[^0-9.-]+/g, ""))
    );

    // Dynamic color assignment for chart labels and series
    const colors = [];
    const numLabels = labels.length;
    const numColors = dynamicColors.length;

    // If there are more data points than colors, cycle through the colors
    for (let i = 0; i < numLabels; i++) {
      colors.push(dynamicColors[i % numColors]);
    }
    setChartOptions({
      chart: {
        type: "pie",
      },
      labels: labels,
      colors: colors,
      title: {
        // text: "Revenue Breakdown by Payment Method",
        align: "center",
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#FFFFFF"],
          fontSize: "14px",
          fontWeight: "bold",
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000000",
          opacity: 0.5,
        },
      },
      legend: {
        position: "right",
        labels: {
          colors: colors,
        },
        itemMargin: {
          horizontal: 15,
          vertical: 5,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            if (val == null || Number.isNaN(val)) return "$0.00";
            return `$${Number.parseFloat(val).toFixed(2)}`;
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    });

    // Filter out invalid values
    const validLabels = labels.filter((label) => label != null);
    const validSeries = seriesData.filter((val) => !Number.isNaN(val) && val != null);

    if (validLabels.length === validSeries.length && validLabels.length > 0) {
      setChartOptions((prev) => ({ ...prev, labels: validLabels }));
      setSeries(validSeries);
    } else {
      setSeries([]);
    }
  };

  const handleRevenueSource = (data, columns) => {
    const rows = getDataRows();
    const revenueData = rows.map((row) => {
      // Extract the revenue value and parse it into a number
      return Number.parseFloat(
        String(row.revenueGenerated || "").replaceAll(/[^0-9.-]+/g, "") || 0
      );
    });

    const labels = rows.map((row) => row.source); // Revenue sources (Admin, App, Web, etc.)

    const totalRevenue = revenueData.reduce(
      (total, value) => total + (value || 0),
      0
    );

    const revenuePercentages = revenueData
      .map((revenue) => {
        if (Number.isNaN(revenue) || revenue == null) return 0;
        return totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0;
      })
      .filter((val) => !Number.isNaN(val) && val != null && val >= 0);

    // Filter labels to match series
    const validLabels = labels
      .filter((label, idx) => label != null && idx < revenuePercentages.length)
      .slice(0, revenuePercentages.length);
    const validSeries = revenuePercentages.slice(0, validLabels.length);

    setChartOptions({
      chart: {
        type: "pie",
      },
      labels: validLabels,
      title: {
        // text: 'Revenue Source Breakdown',
        align: "center",
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#FFFFFF"],
          fontSize: "14px",
          fontWeight: "bold",
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000000",
          opacity: 0.5,
        },
      },
      legend: {
        position: "right",
        labels: {
          colors: validLabels.map(
            (_, index) => dynamicColors[index % dynamicColors.length]
          ),
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            if (val == null || Number.isNaN(val)) return "0.00";
            return Number.parseFloat(val).toFixed(2);
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "right",
            },
          },
        },
      ],
    });

    if (validLabels.length > 0 && validSeries.length > 0) {
      setSeries(validSeries);
    } else {
      setSeries([]);
    }
  };
  const handleCommissionBySource = (data, columns) => {
    const rows = getDataRows();
    const revenueData = rows.map((row) => {
      // Extract the revenue value and parse it into a number
      return Number.parseFloat(
        String(row.total_commission || "").replaceAll(/[^0-9.-]+/g, "") || 0
      );
    });

    const labels = rows.map((row) => row.source); // Revenue sources (Admin, App, Web, etc.)

    const totalRevenue = revenueData.reduce(
      (total, value) => total + (value || 0),
      0
    );

    const revenuePercentages = revenueData
      .map((revenue) => {
        if (Number.isNaN(revenue) || revenue == null) return 0;
        return totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0;
      })
      .filter((val) => !Number.isNaN(val) && val != null && val >= 0);

    // Filter labels to match series
    const validLabels = labels
      .filter((label, idx) => label != null && idx < revenuePercentages.length)
      .slice(0, revenuePercentages.length);
    const validSeries = revenuePercentages.slice(0, validLabels.length);

    setChartOptions({
      chart: {
        type: "pie",
      },
      labels: validLabels,
      title: {
        // text: 'Revenue Source Breakdown',
        align: "center",
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#FFFFFF"],
          fontSize: "14px",
          fontWeight: "bold",
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000000",
          opacity: 0.5,
        },
      },
      legend: {
        position: "right",
        labels: {
          colors: validLabels.map(
            (_, index) => dynamicColors[index % dynamicColors.length]
          ),
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            if (val == null || Number.isNaN(val)) return "0.00";
            return Number.parseFloat(val).toFixed(2);
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "right",
            },
          },
        },
      ],
    });

    if (validLabels.length > 0 && validSeries.length > 0) {
      setSeries(validSeries);
    } else {
      setSeries([]);
    }
  };

  const handleAdrRevparReport = (data, columns) => {
    // console.log(data, columns, 293)
    if (!data?.total_rooms || data.total_rooms === 0) {
      setSeries([]);
      return;
    }

    let labels = [t("Blocked Rooms"), t("Vacant Rooms"), t("Occupied Rooms")];
    const totalRooms = Number.parseFloat(data.total_rooms) || 0;
    const vacantRooms = Number.parseFloat(data.vacant_rooms) || 0;
    const occupiedRooms = Number.parseFloat(data.occupied_rooms) || 0;
    const blockedRooms = Number.parseFloat(data.blocked_rooms) || 0;

    let vacantRoom =
      totalRooms > 0
        ? Number.parseFloat(((vacantRooms * 100) / totalRooms).toFixed(2))
        : 0;
    let occupiedRoom =
      totalRooms > 0
        ? Number.parseFloat(((occupiedRooms * 100) / totalRooms).toFixed(2))
        : 0;
    let blockedRoom =
      totalRooms > 0
        ? Number.parseFloat(((blockedRooms * 100) / totalRooms).toFixed(2))
        : 0;

    const seriesData = [blockedRoom, vacantRoom, occupiedRoom].filter(
      (val) => !Number.isNaN(val) && val != null
    );

    setChartOptions({
      chart: {
        type: "pie",
      },
      labels: labels.slice(0, seriesData.length),
      title: {
        // text: 'Revenue Source Breakdown',
        align: "center",
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#FFFFFF"],
          fontSize: "14px",
          fontWeight: "bold",
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000000",
          opacity: 0.5,
        },
      },
      legend: {
        position: "right",
        labels: {
          colors: labels
            .slice(0, seriesData.length)
            .map((_, index) => dynamicColors[index % dynamicColors.length]),
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            if (val == null || Number.isNaN(val)) return "0.00";
            return Number.parseFloat(val).toFixed(2);
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "right",
            },
          },
        },
      ],
    });

    if (seriesData.length === 3) {
      setSeries(seriesData);
    } else {
      setSeries([]);
    }
  };

  // Dynamic function mapping based on selectedTab
  const tabFunctions = {
    0: handleRevenueBreakdown,
    1: handlePaymentBreakdown,
    2: handleRevenueSource,
    3: handleAdrRevparReport,
    4: handleTransactionBatch,
    5: handleCommissionBySource,
  };

  useEffect(() => {
    const selectedFunction = tabFunctions[selectedTab];
    if (selectedFunction) {
      try {
        selectedFunction(data, columns);
      } catch (error) {
        console.error("Chart error:", error);
        setSeries([]);
      }
    }
  }, [selectedTab, data, columns]);

  
  // Validate before rendering
  const hasValidData =
    Array.isArray(series) &&
    series.length > 0 &&
    series.every((val) => val != null && !Number.isNaN(val)) &&
    chartOptions?.labels &&
    Array.isArray(chartOptions.labels) &&
    chartOptions.labels.length === series.length &&
    chartOptions.labels.every((label) => label != null);

  return (
    <div
      className="revenue-bar-chart d-flex justify-content-center w-100"
      style={{ flexDirection: "column" }}
    >
      <Typography id="font-600" style={{ textAlign: "center" }}>
        {selectedTab === 3 && t("Occupancy & ADR Revpar Report")}
        {selectedTab === 2 && t("Revenue Source Breakdown")}
        {selectedTab === 1 && t("Revenue Breakdown by Payment Method")}
        {selectedTab === 0 && t("Revenue Breakdown by Source")}
        {selectedTab === 4 && t("Transaction Batch Report")}
        {selectedTab === 5 && t("Ota Commission Report")}
      </Typography>
      {hasValidData ? (
        <Chart options={chartOptions} series={series} type="donut" />
      ) : null}
    </div>
  );
};

export default PieChartView;
